import { policyTopics } from '../values';
import { HeroSection } from '../widgets';
import Image from '../../assets/privacy.jpg';
import { forwardRef, useEffect, useRef, useState } from "react";

export function Privacy() {
    return (
        <>
            <HeroSection image={Image} title={'Privacy Policy'} description={'Last Edited: April 19, 2025.'} />
            <Policies />
        </>
    );
}

function Policies() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('introduction');
    const sectionRefs = {
        introduction: useRef(null),
        infoCollection: useRef(null),
        dataUsage: useRef(null),
        dataStorage: useRef(null),
        rightsChoices: useRef(null),
        payments: useRef(null),
        cookies: useRef(null),
        sharingDisclosure: useRef(null),
        dataRetention: useRef(null),
        childPrivacy: useRef(null),
        policyChanges: useRef(null),
        contact: useRef(null),
    };

    const handleScroll = () => {
        const scrollPosition = window.scrollY + 100;
        for (const section of policyTopics) {
            const element = sectionRefs[section.id].current;
            if (element) {
                const { offsetTop, offsetHeight } = element;
                if (scrollPosition >= offsetTop &&
                    scrollPosition < offsetTop + offsetHeight) {
                    setActiveSection(section.id);
                    break;
                }
            }
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);//eslint-disable-next-line
    }, []);

    const scrollToSection = (sectionId) => {
        setActiveSection(sectionId);
        sectionRefs[sectionId].current.scrollIntoView({ behavior: 'smooth' });
        setIsMenuOpen(false);
    };

    return (
        <div className="min-h-screen w-full flex flex-col md:flex-row">
            <div className="md:w-1/5 md:h-screen md:border-r border-gray-300 p-4 md:p-6 overflow-y-auto">
                <button className="md:hidden text-center w-full mb-4 font-bold" onClick={() => setIsMenuOpen(!isMenuOpen)}>{isMenuOpen ? "Close Menu" : "Open Menu"}</button>
                <ul className={`space-y-3 ${isMenuOpen ? 'block' : 'hidden md:block'}`}>
                    {policyTopics.map((topic) => <li key={topic.id} className={`cursor-pointer p-2 rounded transition-colors ${activeSection === topic.id ? 'font-bold' : 'hover:bg-gray-100'}`} onClick={() => scrollToSection(topic.id)}>{topic.title}</li>)}
                </ul>
            </div>
            <div className="md:w-4/5 h-full overflow-y-auto py-4 md:py-8 px-4 md:px-0">
                <div className="max-w-3xl mx-auto">
                    <Introduction ref={sectionRefs.introduction} />
                    <Information ref={sectionRefs.infoCollection} />
                    <DataUsage ref={sectionRefs.dataUsage} />
                    <DataStorage ref={sectionRefs.dataStorage} />
                    <PaymentsSubscriptions ref={sectionRefs.payments} />
                    <Cookies ref={sectionRefs.cookies} />
                    <SharingDisclosure ref={sectionRefs.sharingDisclosure} />
                    <RightsChoices ref={sectionRefs.rightsChoices} />
                    <DataRetention ref={sectionRefs.dataRetention} />
                    <ChildPrivacy ref={sectionRefs.childPrivacy} />
                    <PolicyChanges ref={sectionRefs.policyChanges} />
                    <Contact ref={sectionRefs.contact} />
                </div>
            </div>
        </div>
    );
}

const Introduction = forwardRef((props, ref) => {
    return (
        <section ref={ref}>
            <div className="mb-8 md:mb-12">
                <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Introduction</h2>
                <p className="mb-3 md:mb-4 text-sm md:text-base">Thank you for choosing Corella — your unified platform for email, tasks, communication, and productivity.</p>
                <p className="mb-3 md:mb-4 text-sm md:text-base">At Corella, we are committed to protecting your personal data and respecting your privacy. This Privacy Policy explains what information we collect, how we use it, and the choices available to you regarding your data.</p>
            </div>
            <div className="mb-8 md:mb-12">
                <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Scope of this Privacy Policy</h2>
                <p className="mb-3 md:mb-4 text-sm md:text-base">This Privacy Policy applies to your use of the Corella web application and related services, including any integrations with third-party platforms (such as Google Workspace and Microsoft 365).</p>
            </div>
        </section>
    )
})

const Information = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Information We Collect</h2>
            <ul className="list-disc pl-7 mb-4">
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">Account Information</h2>
                    <p className="mb-4">When you create a Corella account, we collect:</p>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">Full name</li>
                        <li className="mb-2">Email Address</li>
                        <li className="mb-2">Profile Preferences</li>
                    </ul>
                </li>
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">Third-Party Integrations</h2>
                    <p className="mb-4">When you authorize access to external services, we collect data from:</p>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">Google Workspace: Gmail metadata, calendar events, file metadata</li>
                        <li className="mb-2">Microsoft 365 & Teams: Outlook email metadata, calendar meetings, and Teams schedules</li>
                    </ul>
                    <p className="mb-4">We do not store email or file content from these platforms.</p>
                </li>
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">Platform Activity</h2>
                    <p className="mb-4">We collect data related to:</p>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">Tasks you create</li>
                        <li className="mb-2">Messages and chats</li>
                        <li className="mb-2">Events and reminders</li>
                        <li className="mb-2">Interactions within the platform (clicks, views, etc.)</li>
                    </ul>
                </li>
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">Technical Data</h2>
                    <p className="mb-4">We collect:</p>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">IP address</li>
                        <li className="mb-2">Browser and device information</li>
                        <li className="mb-2">Log data and diagnostics for support and analytics</li>
                    </ul>
                </li>
            </ul>
        </section>
    )
})

const DataUsage = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">How We Use Your Information</h2>
            <p className="mb-4">We use the data to:</p>
            <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Provide core functionality of the service</li>
                <li className="mb-2">Authenticate and secure your account</li>
                <li className="mb-2">Synchronize tasks, calendar events, and messages</li>
                <li className="mb-2">Analyze product usage to improve performance</li>
                <li className="mb-2">Provide customer support and service updates</li>
            </ul>
            <p className="mb-4">We do not sell your personal data or use it for advertising.</p>
        </section>
    )
})

const DataStorage = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Data Storage and Security</h2>
            <ul className="list-disc pl-7 mb-4">
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">Where Your Data Lives</h2>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">OAuth tokens and preferences are securely encrypted.</li>
                        <li className="mb-2">Email, files, and meeting content are processed via secure APIs from Google and Microsoft and are not stored in our database.</li>
                    </ul>
                </li>
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">How We Protect Your Data</h2>
                    <p className="mb-4">We use:</p>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">TLS encryption</li>
                        <li className="mb-2">Encrypted storage at rest</li>
                        <li className="mb-2">Role-based access control (RBAC)</li>
                        <li className="mb-2">Security audits and regular testing</li>
                    </ul>
                    <p className="mb-4">We do not store email or file content from these platforms.</p>
                </li>
            </ul>
        </section>
    )
})

const PaymentsSubscriptions = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Information We Collect</h2>
            <ul className="list-disc pl-7 mb-4">
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">Account Information</h2>
                    <p className="mb-4">When you create a Corella account, we collect:</p>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">Full name</li>
                        <li className="mb-2">Email Address</li>
                        <li className="mb-2">Profile Preferences</li>
                    </ul>
                </li>
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">Third-Party Integrations</h2>
                    <p className="mb-4">When you authorize access to external services, we collect data from:</p>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">Google Workspace: Gmail metadata, calendar events, file metadata</li>
                        <li className="mb-2">Microsoft 365 & Teams: Outlook email metadata, calendar meetings, and Teams schedules</li>
                    </ul>
                    <p className="mb-4">We do not store email or file content from these platforms.</p>
                </li>
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">Platform Activity</h2>
                    <p className="mb-4">We collect data related to:</p>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">Tasks you create</li>
                        <li className="mb-2">Messages and chats</li>
                        <li className="mb-2">Events and reminders</li>
                        <li className="mb-2">Interactions within the platform (clicks, views, etc.)</li>
                    </ul>
                </li>
                <li className="mb-2">
                    <h2 className="text-2xl font-semibold mb-4">Technical Data</h2>
                    <p className="mb-4">We collect:</p>
                    <ul className="list-disc pl-7 mb-4">
                        <li className="mb-2">IP address</li>
                        <li className="mb-2">Browser and device information</li>
                        <li className="mb-2">Log data and diagnostics for support and analytics</li>
                    </ul>
                </li>
            </ul>
        </section>
    )
})

const Cookies = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Cookies and Tracking Technologies</h2>
            <p className="mb-4">We use cookies for:</p>
            <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">User session management</li>
                <li className="mb-2">Platform customization</li>
                <li className="mb-2">Analytics and performance optimization</li>
            </ul>
            <p className="mb-4">You can manage cookies in your browser settings.</p>
        </section>
    )
})

const SharingDisclosure = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Sharing and Disclosure</h2>
            <p className="mb-4">We use cookies for:</p>
            <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">User session management</li>
                <li className="mb-2">Platform customization</li>
                <li className="mb-2">Analytics and performance optimization</li>
            </ul>
            <p className="mb-4">We never sell or lease your data.</p>
        </section>
    )
})

const RightsChoices = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Your Rights and Choices</h2>
            <p className="mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Access and update your data</li>
                <li className="mb-2">Request deletion of your data</li>
                <li className="mb-2">Withdraw consent for third-party integration</li>
                <li className="mb-2">Export your data (data portability)</li>
            </ul>
            <p className="mb-4">Contact us at <a href='mailto:contact@corella.work' className='text-blue-600'>contact@corella.work</a> to make a request.</p>
        </section>
    )
})

const DataRetention = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Data Retention</h2>
            <p className="mb-4">We retain user data as long as needed to provide our services. If you delete your account, associated data will be deleted or anonymized unless legal retention is required.</p>
        </section>
    )
})

const ChildPrivacy = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>
            <p className="mb-4">Corella is not intended for use by children under 13. We do not knowingly collect data from minors.</p>
        </section>
    )
})

const PolicyChanges = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Changes to this Policy</h2>
            <p className="mb-4">We may occasionally update this Privacy Policy. Significant changes will be notified via email or in-app notification.</p>
        </section>
    )
})

const Contact = forwardRef((props, ref) => {
    return (
        <section ref={ref} className="border-b border-gray-100">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Contact Us</h2>
            <p className="text-gray-600 leading-relaxed mb-4">For any privacy concerns, please contact us at:</p>
            <ul className="pl-8 list-disc text-gray-600 space-y-2">
                <li><a href='mailto:contact@corella.work' className='text-blue-600'>contact@corella.work</a></li>
                <li><a href='/' className=' text-blue-600'>www.corella.work</a></li>
            </ul>
        </section >
    )
})